import { identity } from "@suse-o11y/identity";
import { isPresent } from "@suse-o11y/is-present";
import { createSelector } from "reselect";

import { createDeepEqualSelector } from "../../../generic/redux/SelectorUtils";
import { matchPath } from "../../../generic/routing";
import type { RouteResult } from "../../../routes/RouterUtils";
import { COMPONENT_PATH, type StackPackNavigationParams, VIEW_PATH } from "../../../routes/Routes";
import {
	type StackPacksRouteParams,
	getStackPackNavigationParams,
	getStackPacksRouteParams,
} from "../../../routes/stackPacks/StackPacksRoute";
import type { RootState } from "../RootState";

import type { LocationState } from "./RouteReducer";
import { decodeAllSearchParams } from "./SearchParams";

export const selectCurrentLocation = (state: RootState): LocationState => state.location;

export const selectLocationPathname = createSelector(selectCurrentLocation, (location) => location.pathname ?? "");

export const selectAllSearchParams = createDeepEqualSelector(
	createSelector(selectCurrentLocation, decodeAllSearchParams),
	identity,
);

export const selectViewSlug = createSelector(selectLocationPathname, (pathname) => {
	const match = matchPath(pathname, VIEW_PATH);

	return match?.params.slug;
});

export const selectIsViewPage = createSelector(selectViewSlug, isPresent);

export const selectComponentIdentifier = createSelector(selectLocationPathname, (pathname) => {
	const match = matchPath(pathname, COMPONENT_PATH);

	return match?.params.identifier;
});

export const selectIsComponentPage = createSelector(selectComponentIdentifier, isPresent);

export const selectViewMode = createSelector(selectLocationPathname, (pathname) => {
	const matchComponent = matchPath(pathname, COMPONENT_PATH);
	if (matchComponent != null) {
		return matchComponent.params.mode;
	}

	const matchPathView = matchPath(pathname, VIEW_PATH);
	if (matchPathView != null) {
		return matchPathView.params.mode;
	}

	return undefined;
});

export const selectStackPacksRouteParams: (state: RootState) => RouteResult<StackPacksRouteParams> = createSelector(
	[selectCurrentLocation],
	(location) => getStackPacksRouteParams(location),
);

export const selectStackPacksRouteType = createSelector(selectStackPacksRouteParams, (stackPackRouteParam) =>
	stackPackRouteParam.isRight() ? stackPackRouteParam.value.type : undefined,
);

export const selectStackPackNavigationParams: (state: RootState) => RouteResult<StackPackNavigationParams> =
	createSelector([selectCurrentLocation], (location) => getStackPackNavigationParams(location));
